// Discrete Math Calculators helpers: multifactorials, permutation parity,
// circular permutations, Pascal's triangle rows, combinations/permutations
// with repetition, derangements, Catalan/Stirling/Bell/Lucas numbers, and
// integer partitions. Basic combinations (nCr), permutations (nPr), and
// factorial already live in lib/tools/probabilityRandomCalculators.ts, and
// the Fibonacci sequence already lives in
// lib/tools/factorsNumberTheoryCalculators.ts - this file deliberately
// avoids re-implementing those and instead covers deeper combinatorics
// topics.
import { t } from './i18n'

function factorialBig(n: number): bigint {
    let result = BigInt(1)
    for (let i = BigInt(2); i <= BigInt(n); i++) result *= i
    return result
}
function combinationBig(n: number, r: number): bigint {
    if (r < 0 || r > n) return BigInt(0)
    let num = BigInt(1)
    let den = BigInt(1)
    for (let i = 0; i < r; i++) {
        num *= BigInt(n - i)
        den *= BigInt(i + 1)
    }
    return num / den
}

// ============================================================
// 1. Multifactorial Calculator
// ============================================================
export function multifactorialCalculator(params: { number: unknown; k: unknown }): { result: string } {
    const n = Math.max(Math.min(Math.round(Number(params.number)) || 0, 1000), 0)
    const k = Math.max(Math.round(Number(params.k)) || 1, 1)
    let result = BigInt(1)
    let x = BigInt(n)
    const step = BigInt(k)
    while (x > BigInt(0)) {
        result *= x
        x -= step
    }
    return { result: result.toString() }
}

// ============================================================
// 2. Even/Odd Permutation Parity Checker
// ============================================================
const EVEN_LABEL: Record<string, string> = {
    en: 'Even', ru: 'Чётная', lv: 'Pāra', pl: 'Parzysta', es: 'Par', fr: 'Paire', it: 'Pari', de: 'Gerade',
}
const ODD_LABEL: Record<string, string> = {
    en: 'Odd', ru: 'Нечётная', lv: 'Nepāra', pl: 'Nieparzysta', es: 'Impar', fr: 'Impaire', it: 'Dispari', de: 'Ungerade',
}
export function permutationParityChecker(params: { permutation: unknown; language: unknown }): { parity_result: string; inversion_count: number } {
    const perm = String(params.permutation || '')
        .split(/[,\s]+/)
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
        .map(Number)
        .filter((v) => !isNaN(v))
        .slice(0, 1000)
    let inversions = 0
    for (let i = 0; i < perm.length; i++) {
        for (let j = i + 1; j < perm.length; j++) {
            if (perm[i] > perm[j]) inversions++
        }
    }
    const bucket = inversions % 2 === 0 ? EVEN_LABEL : ODD_LABEL
    return { parity_result: t(bucket, params.language), inversion_count: inversions }
}

// ============================================================
// 3. Circular Permutations Calculator
// ============================================================
export function circularPermutationsCalculator(params: { n: unknown }): { directed_arrangements: string; undirected_arrangements: string } {
    const n = Math.max(Math.min(Math.round(Number(params.n)) || 1, 1000), 1)
    const directed = factorialBig(Math.max(n - 1, 0))
    const undirected = n >= 3 ? directed / BigInt(2) : directed
    return { directed_arrangements: directed.toString(), undirected_arrangements: undirected.toString() }
}

// ============================================================
// 4. Pascal's Triangle Row Generator
// ============================================================
export function pascalTriangleRowGenerator(params: { row_number: unknown }): { row_values: string } {
    const n = Math.max(Math.min(Math.round(Number(params.row_number)) || 0, 200), 0)
    const row: bigint[] = [BigInt(1)]
    for (let k = 1; k <= n; k++) {
        row.push((row[k - 1] * BigInt(n - k + 1)) / BigInt(k))
    }
    return { row_values: row.map(String).join(', ') }
}

// ============================================================
// 5. Combinations with Repetition Calculator
// ============================================================
export function combinationsWithRepetitionCalculator(params: { n: unknown; r: unknown }): { result: string } {
    const n = Math.max(Math.min(Math.round(Number(params.n)) || 1, 1000), 1)
    const r = Math.max(Math.min(Math.round(Number(params.r)) || 0, 1000), 0)
    return { result: combinationBig(n + r - 1, r).toString() }
}

// ============================================================
// 6. Permutations with Repetition (Multiset) Calculator
// ============================================================
export function permutationsWithRepetitionCalculator(params: { total_items: unknown; group_sizes: unknown }): { result: string } {
    const total = Math.max(Math.min(Math.round(Number(params.total_items)) || 1, 1000), 1)
    const groups = String(params.group_sizes || '')
        .split(/[,\s]+/)
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
        .map((v) => Math.max(Math.round(Number(v)) || 0, 0))
    let result = factorialBig(total)
    for (const g of groups) result /= factorialBig(g)
    return { result: result.toString() }
}

// ============================================================
// 7. Derangement Number Calculator
// ============================================================
export function derangementCalculator(params: { n: unknown }): { result: string } {
    const n = Math.max(Math.min(Math.round(Number(params.n)) || 0, 1000), 0)
    if (n === 0) return { result: '1' }
    if (n === 1) return { result: '0' }
    let d0 = BigInt(1)
    let d1 = BigInt(0)
    for (let i = 2; i <= n; i++) {
        const d2 = BigInt(i - 1) * (d1 + d0)
        d0 = d1
        d1 = d2
    }
    return { result: d1.toString() }
}

// ============================================================
// 8. Catalan Number Calculator
// ============================================================
export function catalanNumberCalculator(params: { n: unknown }): { result: string } {
    const n = Math.max(Math.min(Math.round(Number(params.n)) || 0, 500), 0)
    const result = combinationBig(2 * n, n) / BigInt(n + 1)
    return { result: result.toString() }
}

// ============================================================
// 9. Stirling Numbers of the Second Kind Calculator
// ============================================================
export function stirlingSecondKindCalculator(params: { n: unknown; k: unknown }): { result: string } {
    const n = Math.max(Math.min(Math.round(Number(params.n)) || 0, 200), 0)
    const k = Math.max(Math.min(Math.round(Number(params.k)) || 0, 200), 0)
    if (k > n) return { result: '0' }
    const table: bigint[][] = Array.from({ length: n + 1 }, () => new Array(k + 1).fill(BigInt(0)))
    table[0][0] = BigInt(1)
    for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= Math.min(i, k); j++) {
            table[i][j] = BigInt(j) * table[i - 1][j] + table[i - 1][j - 1]
        }
    }
    return { result: table[n][k].toString() }
}

// ============================================================
// 10. Bell Number Calculator
// ============================================================
export function bellNumberCalculator(params: { n: unknown }): { result: string } {
    const n = Math.max(Math.min(Math.round(Number(params.n)) || 0, 200), 0)
    if (n === 0) return { result: '1' }
    let row: bigint[] = [BigInt(1)]
    for (let i = 1; i <= n; i++) {
        const newRow: bigint[] = [row[row.length - 1]]
        for (let j = 0; j < row.length; j++) {
            newRow.push(newRow[j] + row[j])
        }
        row = newRow
    }
    return { result: row[0].toString() }
}

// ============================================================
// 11. Lucas Numbers Calculator
// ============================================================
export function lucasNumbersCalculator(params: { term_count: unknown }): { sequence_list: string; nth_value: string } {
    const count = Math.max(Math.min(Math.round(Number(params.term_count)) || 10, 1000), 1)
    let a = BigInt(2)
    let b = BigInt(1)
    const seq: bigint[] = [a]
    for (let i = 1; i < count; i++) {
        seq.push(b)
        ;[a, b] = [b, a + b]
    }
    return { sequence_list: seq.map(String).join(', '), nth_value: seq[seq.length - 1].toString() }
}

// ============================================================
// 12. Integer Partition Calculator
// ============================================================
export function integerPartitionCalculator(params: { n: unknown }): { result: string } {
    const n = Math.max(Math.min(Math.round(Number(params.n)) || 0, 500), 0)
    const p = new Array(n + 1).fill(BigInt(0))
    p[0] = BigInt(1)
    for (let i = 1; i <= n; i++) {
        for (let j = i; j <= n; j++) {
            p[j] += p[j - i]
        }
    }
    return { result: p[n].toString() }
}
